import React from 'react';
import { both, pipe } from 'ramda';
import { filter, reject } from 'lodash/fp';
import { resetTippingBarbers } from '../register/tipping/actions';
import { resetCustomer } from '../register/customer/actions';
import { ITEM_TYPE } from '../register/constants';
import { resetOrder } from '../register/order/actions';
import { IOS_BRIDGE_PAYMENT_SUCCESS, PAYMENT_TYPE, WEBREADER_PAYMENT_SUCCESS } from '../main/payment/constants';
import { PAYMENT_SUCCESS } from '../register/payment/constants';
import { getTippingBarbers } from '../register/tipping/selectors';
import {
  activeShopCashTipsEnabled,
  activeShopReaderTipsEnabled,
  activeShopGiftCardTipsEnabled,
  getRoleIdType,
} from '../auth/selectors';
import { getPayments } from '../register/payment/reducer/payments/selectors';
import * as validatedSelectors from '../register/validated/selectors';
import { none } from '../common/helpers/functional';
import { getOrderForCheckout, getOrderForWebreaderSale, getOrderForListeningTipping } from '../register/selectors';
import { resetValidation } from '../register/validated/actions';
import {
  checkoutBarberCart,
  checkoutShopCart,
  createBarberCart,
  createShopCart,
  createShopSale,
  createBarberSale,
  updateShopCart,
  updateBarberCart,
} from '../cart/actions';
import { resetPromoCode } from '../register/promo/actions';
import { resetPayments } from '../register/payment/actions';
import { setGiftCard } from '../register/payment/reducer/giftCard/actions';
import { closeChargeModal } from '../register/payment/reducer/chargeModal/actions';
import { setSoldGiftCards, resetSoldGiftCards } from '../register/giftCards/actions';
import Money from '../common/money';
import { ChurnZeroService, RUN_PAYMENT_SUCCESS } from '../../modules/infra/observability/churnzero';
import { CustomerIoTracker, REGISTER_TRANSACTION_COMPLETED } from '../../modules/infra/observability/customer-io';
import { RudderstackTracker, APPOINTMENT_CHARGED } from '../../modules/infra/observability/rudderstack';
import { t } from '../../modules/infra/i18n';

export const getChargeId = (state, action) => {
  if (action.type === IOS_BRIDGE_PAYMENT_SUCCESS) {
    return action.payload;
  }
  if (action.type === PAYMENT_SUCCESS) {
    return action.payload.payments[0].chargeId || action.payload.payments[0].id;
  }
  if (action.type === WEBREADER_PAYMENT_SUCCESS) {
    return action.payload.payments[0].chargeId;
  }

  return action.payload[0].chargeId || action.payload[0].id;
};

export const getTotal = state => validatedSelectors.getTotal(state);

export const shouldAssignEmptyCashPayment = both(
  activeShopCashTipsEnabled,
  pipe(getPayments, none({ type: PAYMENT_TYPE.cash })),
);

export const shouldProcessCharge = (state) => {
  if (!getTippingBarbers(state).length) return true;

  const cashTipsEnabled = activeShopCashTipsEnabled(state);
  const readerTipsEnabled = activeShopReaderTipsEnabled(state);
  const giftCardTipsEnabled = activeShopGiftCardTipsEnabled(state);
  const shouldSkipTipOnTerminal = validatedSelectors.shouldSkipOnTerminalTip(state);

  const payments = pipe(
    getPayments,
    reject((item) => {
      switch (item.type) {
        case PAYMENT_TYPE.cash:
          return !cashTipsEnabled;
        case PAYMENT_TYPE.giftCard:
          return !giftCardTipsEnabled;
        case PAYMENT_TYPE.reader:
          // skip tips when the pos tips are disabled or when tip needs to be left on the terminal
          return !readerTipsEnabled || !shouldSkipTipOnTerminal;
        case PAYMENT_TYPE.readerless:
        case PAYMENT_TYPE.card:
          return !readerTipsEnabled;
        default:
          return false;
      }
    }),
  )(state);
  return !payments.length;
};

export const serializeOrder = store => getOrderForCheckout(store.getState());
export const serializeWebreaderOrder = store => getOrderForWebreaderSale(store.getState());
export const serializeOrderForListening = store => getOrderForListeningTipping(store.getState());

export const shopOrBarberAction = (store, shopAction, barberAction) => {
  const shopOrBarber = getRoleIdType(store.getState());
  if (shopOrBarber.type === 'barber') {
    return barberAction.bind(null, shopOrBarber.id);
  }
  return shopAction.bind(null, shopOrBarber.id);
};

export const createWebreaderSale = (store, order) => shopOrBarberAction(store, createShopSale, createBarberSale)(order);

export const createCart = (store, order) => shopOrBarberAction(store, createShopCart, createBarberCart)(order);
// eslint-disable-next-line max-len
export const updateCart = (store, cartId, order) => shopOrBarberAction(store, updateShopCart, updateBarberCart)(cartId, order);
export const checkoutCart = (
  store,
  id,
  tips,
) => shopOrBarberAction(store, checkoutShopCart, checkoutBarberCart)(id, tips);

export const resetSale = (store) => {
  store.dispatch(resetOrder());
  store.dispatch(resetPromoCode());
  store.dispatch(resetCustomer());
  store.dispatch(resetPayments());
  store.dispatch(setGiftCard());
  store.dispatch(resetTippingBarbers());
  store.dispatch(closeChargeModal());
  store.dispatch(resetValidation());
  store.dispatch(resetSoldGiftCards());
};

export const storeGiftCardCodes = sale => (dispatch) => {
  const giftCardItems = filter({ type: ITEM_TYPE.giftCard }, sale.items);
  dispatch(setSoldGiftCards(giftCardItems));
};

export const getGcSuccessMsg = (giftCard, isTopUp) => (
  isTopUp
    ? (
      <span>
        <Money>{giftCard.priceWithTaxes}</Money>
        &nbsp;{t('register:gift_card.top_up_success_message')}&nbsp;
        <code>{giftCard.name}</code>
      </span>
    )
    : (
      <span>
        {t('register:gift_card.sale_success_message')} <code>{giftCard.name}</code>
      </span>
    )
);

export const trackPaymentSuccess = (payments, hasAppointment) => {
  const paymentTypes = payments && payments.length
    ? payments.map(payment => payment.type).join(' + ')
    : null;

  ChurnZeroService.track(RUN_PAYMENT_SUCCESS, paymentTypes);

  if (hasAppointment) {
    RudderstackTracker.getInstance().track(APPOINTMENT_CHARGED);
  }
  CustomerIoTracker.getInstance().track(REGISTER_TRANSACTION_COMPLETED);
};
